import { ShoppingCart } from "@mui/icons-material";
import { AppBar, Avatar, Badge, Box, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Switch, Toolbar, Typography, styled } from "@mui/material";
import { Link, NavLink } from "react-router-dom";
import { useAppSelector } from "../store/configureStore";
import SignedInMenu from "./SignedInMenu";
import { useState } from "react";
import MenuIcon from '@mui/icons-material/Menu';
import DatasetIcon from '@mui/icons-material/Dataset';
import InventoryIcon from '@mui/icons-material/Inventory';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import LoginIcon from '@mui/icons-material/Login';
import HowToRegIcon from '@mui/icons-material/HowToReg';
// import { ListAltIcon, DatasetIcon, InventoryIcon, PriceChangeIcon, LoginIcon, LogoutIcon, HowToRegIcon } from '@mui/icons-material';


interface Props {
    darkMode: boolean;
    handleThemeChange: () => void;
}

const midLinks = [
    { title: "catalog", path: "/catalog" },
    // { title: "about", path: "/about" },
    // { title: "contact", path: "/contact" },
    { title: "budget", path: "/budget" },
];

const rightLinks = [
    { title: "login", path: "/login" },
    { title: "signup", path: "/signup" },
];

const navStyles = {
    color: "inherit",
    textDecoration: "none",
    typography: "h6",
    "&:hover": {
        color: "success.light",
    },
    "&.active": {
        color: "warning.main",
    },
    display: { xs: 'none', sm: 'block' },
};


// Adding a switch for light and dark mode
const MaterialUISwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    "& .MuiSwitch-switchBase": {
        margin: 1,
        padding: 0,
        transform: "translateX(6px)",
        "&.Mui-checked": {
            color: "#fff",
            transform: "translateX(22px)",
            "& .MuiSwitch-thumb:before": {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
                    "#fff"
                )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
            },
            "& + .MuiSwitch-track": {
                opacity: 1,
                backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
            },
        },
    },
    "& .MuiSwitch-thumb": {
        backgroundColor: theme.palette.mode === "dark" ? "#003892" : "#001e3c",
        width: 32,
        height: 32,
        "&:before": {
            content: "''",
            position: "absolute",
            width: "100%",
            height: "100%",
            left: 0,
            top: 0,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
                "#fff"
            )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
        },
    },
    "& .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
        borderRadius: 20 / 2,
    },
}));

const drawerWidth = 240;

export default function Header({ handleThemeChange, darkMode }: Props) {
    const { basket } = useAppSelector((state) => state.basket);
    const { user } = useAppSelector((state) => state.account);
    const itemCount = basket?.items.reduce((sum, item) => sum + item.quantity, 0);

    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen((prevState) => !prevState);
    };

    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
            <Avatar
                sx={{
                    bgcolor: "warning.dark",
                    width: 240,
                    height: "fit-content",
                    marginRight: 2,
                    boxShadow: "0px 0px 10px rgba(255, 152, 0, 0.5)",
                    padding: "5px",
                }}
                variant="square"
            >
                <Typography
                    variant="h6"
                    noWrap
                    component={NavLink}
                    to="/"
                    sx={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        letterSpacing: ".3rem",
                        color: "inherit",
                        textDecoration: "none",
                        margin: "0px 5px",
                        "&:hover": {
                            color: "yellow",
                        },
                    }}
                >
                    GoShop
                </Typography>
            </Avatar>
            <Divider />

            <List>
                {midLinks.map(({ title, path }) => (

                    <ListItem component={NavLink} to={path} key={path} >
                        <ListItemIcon>
                            {title === 'catalog' ? <DatasetIcon /> : <PriceChangeIcon />}
                        </ListItemIcon>
                        <ListItemButton sx={{ textAlign: 'center' }}>
                            <ListItemText primary={title.toUpperCase()} />
                        </ListItemButton>
                    </ListItem>
                ))}
                {user && user.roles?.includes('Admin') &&
                    <ListItem component={NavLink} to={'/inventory'}>
                        <ListItemIcon>
                            <InventoryIcon />
                        </ListItemIcon>
                        <ListItemButton sx={{ textAlign: 'center' }}>
                            <ListItemText primary='INVENTORY' />
                        </ListItemButton>
                    </ListItem>
                }
            </List>
            <Divider />

            <List>
                {user ? (
                    <SignedInMenu variant="list" showOnly={["My orders", "Logout"]} />
                ) : (
                    <List>
                        {rightLinks.map(({ title, path }) => (
                            <ListItem component={NavLink} to={path} key={path}>
                                <ListItemIcon>
                                    {title === 'login' ? <LoginIcon /> : <HowToRegIcon />}
                                </ListItemIcon>
                                <ListItemButton sx={{ textAlign: 'center' }}>
                                    <ListItemText primary={title.toUpperCase()} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}

            </List>


        </Box>
    );

    return (
        <>
            <AppBar position="static" sx={{ mb: 4, bgcolor: "#3d3c3c" }} component="nav">
                <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box display="flex" alignItems="center">
                        <Avatar
                            sx={{
                                bgcolor: "warning.dark",
                                width: "fit-content",
                                height: "fit-content",
                                marginRight: 2,
                                boxShadow: "0px 0px 10px rgba(255, 152, 0, 0.5)",
                                padding: "5px",
                                display: { xs: 'none', sm: 'block' },
                            }}
                            variant="square"
                        >
                            <Typography
                                variant="h6"
                                noWrap
                                component={NavLink}
                                to="/"
                                sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    letterSpacing: ".3rem",
                                    color: "inherit",
                                    textDecoration: "none",
                                    margin: "0px 5px",
                                    "&:hover": {
                                        color: "yellow",
                                    },
                                }}
                            >
                                GoShop
                            </Typography>
                        </Avatar>
                        {<MaterialUISwitch sx={{ m: 1 }} onChange={handleThemeChange} />}
                        {/* <Switch  checked={darkMode} /> */}
                    </Box>

                    <List sx={{ display: 'flex' }}>
                        {midLinks.map(({ title, path }) => (
                            <ListItem component={NavLink} to={path} key={path} sx={navStyles}>
                                {title.toUpperCase()}
                            </ListItem>
                        ))}
                        {user && user.roles?.includes('Admin') &&
                            <ListItem component={NavLink} to={'/inventory'} sx={navStyles}>
                                INVENTORY
                            </ListItem>
                        }
                    </List>
                    <Box display="flex" alignItems="center">
                        <IconButton component={Link} to="/basket" size="large" edge="start" color="inherit" sx={{ mr: 2 }}>
                            <Badge badgeContent={itemCount} color="warning">
                                <ShoppingCart />
                            </Badge>
                        </IconButton>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {user ? (
                                <SignedInMenu variant="button" />
                            ) : (
                                <List sx={{ display: "flex" }}>
                                    {rightLinks.map(({ title, path }) => (
                                        <ListItem component={NavLink} to={path} key={path} sx={navStyles}>
                                            {title.toUpperCase()}
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>
            <nav>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
            </nav>
        </>
    );
}